import { Route, Routes } from 'react-router-dom';

import { AuthCallback } from '@/components/auth/AuthCallback';
import AccountLayout from '@/components/layout/AccountLayout';
import FocusedLayout from '@/components/layout/FocusedLayout';
import CrossHostRedirect from '@/routing/CrossHostRedirect';
import { lazyRoute } from '@/utils/lazyRoute';

const StudioLanding = lazyRoute(() => import('@/pages/StudioLanding'));
const LightingStudioDashboard = lazyRoute(() => import('@/pages/LightingStudioDashboard'));
const LightingStudioEditor = lazyRoute(() => import('@/pages/LightingStudioEditor'));
const LightingStudioSettings = lazyRoute(() => import('@/pages/LightingStudioSettings'));
const LightingInvitationAccept = lazyRoute(() => import('@/pages/LightingInvitationAccept'));
const LightingPresentation = lazyRoute(() => import('@/pages/LightingPresentation'));
const Auth = lazyRoute(() => import('@/pages/Auth'));
const CustomSignUp = lazyRoute(() => import('@/pages/CustomSignUp'));
const VerifyEmail = lazyRoute(() => import('@/pages/VerifyEmail'));
const ForgotPassword = lazyRoute(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazyRoute(() => import('@/pages/ResetPassword'));

/**
 * Route tree for studio.lightshunt.com. The studio host keeps its own Supabase
 * session (sessions live in per-origin localStorage), so it mounts the auth
 * pages itself. Every other path belongs to the marketplace and hops back.
 */
const StudioRoutes = () => (
  <Routes>
    <Route path="/" element={<StudioLanding />} />

    <Route element={<FocusedLayout chrome="bare" />}>
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/signup" element={<CustomSignUp />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
    </Route>

    <Route element={<AccountLayout product="studio" />}>
      <Route path="/projects" element={<LightingStudioDashboard />} />
      <Route path="/settings" element={<LightingStudioSettings />} />
    </Route>
    <Route element={<AccountLayout product="studio" fullBleed />}>
      <Route path="/projects/:projectId" element={<LightingStudioEditor />} />
    </Route>

    {/* Invitation accept: the token plus a signed-in matching email is the entry. */}
    <Route element={<FocusedLayout />}>
      <Route path="/invitations/:token" element={<LightingInvitationAccept />} />
    </Route>
    {/* Client presentation: the unguessable link is the access secret, and the
        homeowner sees only the installer's brand. */}
    <Route element={<FocusedLayout chrome="bare" />}>
      <Route path="/p/:publicToken" element={<LightingPresentation />} />
    </Route>

    {/* Paths from the old /studio mount still resolve after the move. */}
    <Route path="/studio" element={<CrossHostRedirect to="studio" />} />
    <Route path="/studio/*" element={<CrossHostRedirect to="studio" />} />
    <Route path="*" element={<CrossHostRedirect to="main" />} />
  </Routes>
);

export default StudioRoutes;
